import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Alert,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { BugReport, ExpandMore, Warning, CheckCircle } from '@mui/icons-material';
import axios from 'axios';
import { phiTestCases } from '../data/phiTestCases';
import toast from 'react-hot-toast';

const ComplianceTester: React.FC = () => {
  const [testPrompt, setTestPrompt] = useState('');
  const [selectedTest, setSelectedTest] = useState('');
  const [result, setResult] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [framework, setFramework] = useState('HIPAA');

  const handleTest = async () => {
    if (!testPrompt.trim()) {
      toast.error('Please enter a prompt to test');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post('/api/prompts/analyze', {
        prompt: testPrompt
      });
      
      // Transform the response to match our expected format
      const analysisData = response.data.analysis || {};
      const complianceData = analysisData.compliance || {};
      
      const transformedResult = {
        compliance_score: complianceData.phi_detected ? 
          Math.max(0, 100 - (complianceData.phi_confidence * 100)) : 100,
        risk_level: complianceData.phi_detected ? 
          (complianceData.phi_confidence > 0.7 ? 'critical' : 
           complianceData.phi_confidence > 0.5 ? 'high' : 'medium') : 'low',
        detected_phi: complianceData.phi_categories?.map((category: string, idx: number) => ({
          type: category,
          value: 'Detected in text',
          confidence: complianceData.phi_confidence || 0,
          position: { start: 0, end: testPrompt.length }
        })) || [],
        violations: complianceData.phi_detected ? 
          [`PHI detected: ${complianceData.phi_categories?.join(', ')}`] : [],
        recommended_action: complianceData.phi_detected ?
          'Redact or encrypt PHI before processing' : 'Prompt is compliant',
        framework: framework
      };
      
      setResult(transformedResult);
      
      if (transformedResult.detected_phi?.length > 0) {
        toast.error(`PHI Detected: ${transformedResult.detected_phi.length} categories found`);
      } else {
        toast.success('No PHI detected - Prompt is compliant');
      }
    } catch (error: any) {
      console.error('Test failed:', error);
      toast.error(error.response?.data?.message || 'Failed to analyze prompt');
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const loadTestCase = (testCase: any) => {
    setTestPrompt(testCase.prompt);
    setSelectedTest(testCase.id);
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'error';
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        <BugReport sx={{ mr: 1, verticalAlign: 'middle' }} />
        HIPAA Compliance & PHI Detection Tester
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        Test prompts for Protected Health Information (PHI) and HIPAA compliance violations
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Compliance Framework</InputLabel>
                    <Select
                      value={framework}
                      onChange={(e) => setFramework(e.target.value)}
                      label="Compliance Framework"
                    >
                      <MenuItem value="HIPAA">HIPAA</MenuItem>
                      <MenuItem value="GDPR">GDPR</MenuItem>
                      <MenuItem value="SOC2">SOC 2</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Load Test Case</InputLabel>
                    <Select
                      value={selectedTest}
                      onChange={(e) => {
                        const tc = phiTestCases.find(t => t.id === e.target.value);
                        if (tc) loadTestCase(tc);
                      }}
                      label="Load Test Case"
                    >
                      {phiTestCases.map((tc) => (
                        <MenuItem key={tc.id} value={tc.id}>{tc.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <TextField
                fullWidth
                multiline
                rows={6}
                label="Test Prompt"
                value={testPrompt}
                onChange={(e) => setTestPrompt(e.target.value)}
                placeholder="Enter a prompt to test for PHI and compliance violations..."
                sx={{ mt: 2, mb: 2 }}
              />

              <Button
                variant="contained"
                startIcon={<BugReport />}
                onClick={handleTest}
                disabled={!testPrompt || loading}
                fullWidth
                size="large"
              >
                {loading ? 'Analyzing...' : 'Test PHI Detection'}
              </Button>

              {result && (
                <Box mt={3}>
                  <Divider sx={{ mb: 2 }} />
                  
                  {/* Overall Compliance Score */}
                  <Card variant="outlined" sx={{ mb: 2 }}>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="h6">
                          Compliance Score
                        </Typography>
                        <Chip
                          label={`${(result.compliance_score || 0).toFixed(1)}%`}
                          color={result.compliance_score >= 80 ? 'success' : result.compliance_score >= 50 ? 'warning' : 'error'}
                        />
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={result.compliance_score || 0}
                        color={result.compliance_score >= 80 ? 'success' : result.compliance_score >= 50 ? 'warning' : 'error'}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                      <Box display="flex" alignItems="center" mt={2}>
                        {result.risk_level === 'high' || result.risk_level === 'critical' ? (
                          <Warning color="error" sx={{ mr: 1 }} />
                        ) : (
                          <CheckCircle color="success" sx={{ mr: 1 }} />
                        )}
                        <Typography variant="body2">
                          Risk Level: <strong>{result.risk_level?.toUpperCase() || 'UNKNOWN'}</strong>
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>

                  {/* Detected PHI */}
                  {result.detected_phi && result.detected_phi.length > 0 && (
                    <Accordion defaultExpanded>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography variant="h6">
                          Detected PHI ({result.detected_phi.length} items)
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <List>
                          {result.detected_phi.map((phi: any, idx: number) => (
                            <ListItem key={idx} divider>
                              <ListItemText
                                primary={
                                  <Box display="flex" alignItems="center" gap={1}>
                                    <Chip
                                      label={phi.type}
                                      size="small"
                                      color="error"
                                    />
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                      "{phi.value}"
                                    </Typography>
                                  </Box>
                                }
                                secondary={
                                  <Box mt={1}>
                                    <Typography variant="caption" display="block">
                                      Confidence: {(phi.confidence * 100).toFixed(1)}%
                                    </Typography>
                                    <Typography variant="caption" display="block">
                                      Position: {phi.position.start} - {phi.position.end}
                                    </Typography>
                                  </Box>
                                }
                              />
                            </ListItem>
                          ))}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  )}

                  {/* Violations */}
                  {result.violations && result.violations.length > 0 && (
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography variant="h6">
                          Compliance Violations ({result.violations.length})
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <List>
                          {result.violations.map((violation: string, idx: number) => (
                            <ListItem key={idx}>
                              <ListItemText
                                primary={
                                  <Box display="flex" alignItems="center" gap={1}>
                                    <Warning color="error" fontSize="small" />
                                    <Typography variant="body2">{violation}</Typography>
                                  </Box>
                                }
                              />
                            </ListItem>
                          ))}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  )}

                  {/* Recommendations */}
                  {result.recommended_action && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Recommended Action:
                      </Typography>
                      <Typography variant="body2">
                        {result.recommended_action}
                      </Typography>
                    </Alert>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Test Cases Library
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Click to load realistic medical scenarios
              </Typography>
              <List sx={{ maxHeight: 600, overflow: 'auto' }}>
                {phiTestCases.map((tc) => (
                  <React.Fragment key={tc.id}>
                    <ListItemButton
                      onClick={() => loadTestCase(tc)}
                      selected={selectedTest === tc.id}
                    >
                      <ListItemText
                        primary={tc.name}
                        secondary={
                          <Box>
                            <Typography variant="caption" display="block" color="textSecondary">
                              {tc.description}
                            </Typography>
                            <Box mt={0.5}>
                              <Chip
                                label={tc.severity.toUpperCase()}
                                size="small"
                                color={getSeverityColor(tc.severity) as any}
                              />
                              {tc.expectedDetections.length > 0 && (
                                <Chip
                                  label={`${tc.expectedDetections.length} PHI types`}
                                  size="small"
                                  variant="outlined"
                                  sx={{ ml: 0.5 }}
                                />
                              )}
                            </Box>
                          </Box>
                        }
                      />
                    </ListItemButton>
                    <Divider />
                  </React.Fragment>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ComplianceTester;
